import { useState } from 'react';
import Card from '../../../components/base/Card';

export default function WhyChooseUs() {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const features = [
    {
      id: 'lowest-prices',
      icon: 'ri-price-tag-3-line',
      title: 'Guaranteed Lowest Prices',
      description: 'We guarantee the best flight rates available anywhere. Find a lower price? We\'ll match it and give you ₹500 extra credit for your next booking.',
      color: 'from-green-100 to-green-200',
      iconColor: 'text-green-600',
      modalContent: {
        title: 'Guaranteed Lowest Prices',
        subtitle: 'Our Price Match Promise',
        details: [
          'We continuously monitor flight prices across 200+ booking platforms',
          'Real-time price comparison ensures you get the best deals',
          'If you find a lower price within 24 hours, we\'ll match it + ₹500 bonus',
          'No hidden fees - transparent pricing with all taxes included',
          'Exclusive airline partnerships for special discounted rates'
        ],
        stats: [
          { label: 'Average Savings', value: '₹2,500', icon: 'ri-money-rupee-circle-line' },
          { label: 'Price Matches', value: '99.2%', icon: 'ri-check-double-line' },
          { label: 'Customer Savings', value: '₹500Cr+', icon: 'ri-bar-chart-line' }
        ]
      }
    },
    {
      id: 'instant-booking',
      icon: 'ri-flashlight-line',
      title: 'Instant Booking Confirmation',
      description: 'Get immediate flight confirmation and e-tickets within seconds of payment. No waiting, no delays - just instant booking confirmation.',
      color: 'from-blue-100 to-blue-200',
      iconColor: 'text-blue-600',
      modalContent: {
        title: 'Instant Booking Confirmation',
        subtitle: 'Lightning-Fast Booking Process',
        details: [
          'Automated booking system processes confirmations in under 30 seconds',
          'Real-time seat availability and instant seat selection',
          'E-tickets delivered immediately to email and SMS',
          'PNR generation and airline confirmation within minutes',
          'Mobile boarding passes available instantly after booking'
        ],
        stats: [
          { label: 'Confirmation Time', value: '<30 sec', icon: 'ri-time-line' },
          { label: 'Success Rate', value: '99.8%', icon: 'ri-check-line' },
          { label: 'Instant Tickets', value: '1M+', icon: 'ri-ticket-line' }
        ]
      }
    },
    {
      id: 'global-airlines',
      icon: 'ri-global-line',
      title: '500+ Airlines Worldwide',
      description: 'Access flights from over 500 domestic and international airlines. Compare prices, schedules, and find the perfect flight for your journey.',
      color: 'from-purple-100 to-purple-200',
      iconColor: 'text-purple-600',
      modalContent: {
        title: '500+ Airlines Worldwide',
        subtitle: 'Comprehensive Flight Options',
        details: [
          'Partnerships with major international carriers like Emirates, Qatar Airways, Singapore Airlines',
          'Complete coverage of Indian domestic airlines - IndiGo, Air India, SpiceJet, Vistara',
          'Budget airlines to luxury carriers - options for every budget',
          'Regional carriers for remote destinations across India',
          'Codeshare flights for seamless international connections'
        ],
        stats: [
          { label: 'Airlines', value: '500+', icon: 'ri-plane-line' },
          { label: 'Routes', value: '10,000+', icon: 'ri-route-line' },
          { label: 'Destinations', value: '1,200+', icon: 'ri-map-pin-line' }
        ]
      }
    },
    {
      id: 'customer-support',
      icon: 'ri-customer-service-2-line',
      title: '24/7 Customer Support',
      description: 'Round-the-clock assistance from our travel experts. Whether it\'s booking help, flight changes, or travel emergencies - we\'re always here.',
      color: 'from-orange-100 to-orange-200',
      iconColor: 'text-orange-600',
      modalContent: {
        title: '24/7 Customer Support',
        subtitle: 'Always Here When You Need Us',
        details: [
          'Dedicated support team available 365 days a year',
          'Multi-lingual support in Hindi, English, and regional languages',
          'Emergency travel assistance for flight disruptions',
          'Live chat, phone, and email support channels',
          'Average response time under 2 minutes for urgent queries'
        ],
        stats: [
          { label: 'Response Time', value: '<2 min', icon: 'ri-timer-line' },
          { label: 'Resolution Rate', value: '98.5%', icon: 'ri-check-double-line' },
          { label: 'Support Tickets', value: '50K+/month', icon: 'ri-customer-service-line' }
        ]
      }
    },
    {
      id: 'secure-payments',
      icon: 'ri-shield-check-line',
      title: 'Secure & Safe Payments',
      description: 'Your payment information is 100% secure with bank-level encryption. Multiple payment options including UPI, cards, and net banking.',
      color: 'from-red-100 to-red-200',
      iconColor: 'text-red-600',
      modalContent: {
        title: 'Secure & Safe Payments',
        subtitle: 'Bank-Level Security Standards',
        details: [
          'SSL encryption and PCI DSS compliance for all transactions',
          'Multiple payment options: UPI, Cards, Net Banking, Wallets',
          'Two-factor authentication for enhanced security',
          'Secure tokenization - we never store your card details',
          'Instant refunds processed within 5-7 business days'
        ],
        stats: [
          { label: 'Security Rating', value: 'A+', icon: 'ri-shield-check-line' },
          { label: 'Payment Success', value: '99.1%', icon: 'ri-secure-payment-line' },
          { label: 'Fraud Rate', value: '0.01%', icon: 'ri-error-warning-line' }
        ]
      }
    },
    {
      id: 'flexible-booking',
      icon: 'ri-calendar-check-line',
      title: 'Flexible Booking Options',
      description: 'Easy cancellations, date changes, and refunds. Book with confidence knowing you have flexible options if your plans change.',
      color: 'from-indigo-100 to-indigo-200',
      iconColor: 'text-indigo-600',
      modalContent: {
        title: 'Flexible Booking Options',
        subtitle: 'Travel With Complete Peace of Mind',
        details: [
          'Free cancellation within 24 hours of booking (conditions apply)',
          'Easy date change options with minimal fees',
          'Travel insurance options for comprehensive protection',
          'Flexible fare options for worry-free travel planning',
          'COVID-19 related changes handled with special flexibility'
        ],
        stats: [
          { label: 'Free Changes', value: '24 hrs', icon: 'ri-calendar-line' },
          { label: 'Refund Success', value: '96.8%', icon: 'ri-refund-line' },
          { label: 'Processing Time', value: '5-7 days', icon: 'ri-time-line' }
        ]
      }
    }
  ];

  const handleLearnMore = (featureId: string) => {
    setActiveModal(featureId);
  };

  const handleCallNow = () => {
    const message = "Hi MaxJourneys! I'm interested in your flexible booking options and would like to know more about your travel services. Please provide me with details about flight bookings and flexible policies. Thank you!";
    const whatsappUrl = `https://wa.me/919700644857?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const renderModal = () => {
    if (!activeModal) return null;

    const feature = features.find(f => f.id === activeModal);
    if (!feature) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="sticky top-0 bg-white border-b px-8 py-6 flex justify-between items-center">
            <div className="flex items-center">
              <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full mr-4`}>
                <i className={`${feature.icon} text-2xl ${feature.iconColor}`}></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{feature.modalContent.title}</h2>
                <p className="text-gray-600">{feature.modalContent.subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setActiveModal(null)}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {feature.modalContent.stats.map((stat, index) => (
                <div key={index} className="text-center p-6 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-3">
                    <i className={`${stat.icon} text-xl text-blue-600`}></i>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Why This Matters</h3>
              <ul className="space-y-3">
                {feature.modalContent.details.map((detail, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-check-line text-green-600 mr-3 mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Experience the MaxJourneys difference. Book your next flight with complete confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={handleCallNow}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call +91 9700644857
                </button>
                <button 
                  onClick={() => setActiveModal(null)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
            <i className="ri-star-line mr-2"></i>
            Why 1M+ Travelers Choose Us
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            India's Most Trusted Flight Booking Platform
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            With over 1 million successful bookings, we've earned the trust of travelers across India by consistently delivering the best flight deals, instant confirmations, and unmatched customer service.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card key={index} hover className="text-center">
              <div className="p-8">
                <div className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full mx-auto mb-6`}>
                  <i className={`${feature.icon} text-3xl ${feature.iconColor}`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                <button 
                  onClick={() => handleLearnMore(feature.id)}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-400 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-white font-bold py-3 text-sm cursor-pointer whitespace-nowrap rounded-lg"
                >
                  <i className="ri-arrow-right-line mr-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                  Learn More
                </button>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 lg:p-12 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Join 1M+ Happy Travelers
            </h3>
            <p className="text-xl mb-8 text-blue-100">
              Experience the difference with India's #1 flight booking platform. Book your next flight with confidence and save big on every journey.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">1M+</div>
                <div className="text-blue-200 text-sm">Flights Booked</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">98.5%</div>
                <div className="text-blue-200 text-sm">Customer Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">₹500Cr+</div>
                <div className="text-blue-200 text-sm">Money Saved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">4.9★</div>
                <div className="text-blue-200 text-sm">Average Rating</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleCallNow}
                className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call +91 9700644857
              </button>
              <button 
                onClick={() => {
                  const searchSection = document.getElementById('flight-search');
                  if (searchSection) {
                    searchSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-blue-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flight-takeoff-line mr-2"></i>
                Book Flight Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {renderModal()}
    </section>
  );
}
